import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { AlertTriangle, LogOut } from 'lucide-react';

import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { Button } from '@/components/ui/atoms/Button';
import { DataTableOrganism } from '@/components/ui/organisms';
import {
  AccentBarHeading,
  IllustratedEmptyState,
} from '@/components/ui/molecules';
import { SummarizedListSectionTemplate } from '@/components/ui/templates';
import { useAdminSessionsService, type SessionUser } from '../../services/admin-sessions.service';
import {
  createSessionTableColumns,
  mapSessionUserToRow,
  type SessionTableRow,
} from './sessionTable.columns';

export const SessionsManagement = () => {
  const { user } = useAuth0();
  const { listUsers, disconnectUser, disconnectAll } = useAdminSessionsService();

  const [users, setUsers] = useState<SessionUser[]>([]);
  const [loading, setLoading] = useState(true);

  const [target, setTarget] = useState<SessionUser | null>(null);
  const [showDisconnectAll, setShowDisconnectAll] = useState(false);
  const [acting, setActing] = useState(false);

  const currentSub = user?.sub;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listUsers()
      .then((data) => { if (!cancelled) setUsers(data); })
      .catch((err) => {
        if (!cancelled) {
          const fallback = i18n.t('common:toasts.admin.users_load_failed');
          toast.error(err.message && err.message !== i18n.t('common:toasts.admin.users_load_failed', { lng: 'en' }) ? err.message : fallback);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listUsers]);

  const tableRows = useMemo(() => users.map(mapSessionUserToRow), [users]);
  const columns = useMemo(
    () =>
      createSessionTableColumns({
        currentSub,
        onDisconnect: setTarget,
      }),
    [currentSub]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<LogOut />}
          title="No users"
          description="Session users appear here after they sign in."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<LogOut />}
        title="No matching users"
        description="Try another email, name, or Auth0 sub."
      />
    );
  }, [tableRows.length]);

  const handleDisconnectUser = async () => {
    if (!target) return;
    setActing(true);
    try {
      await disconnectUser(target.id);
      toast.success(i18n.t('common:toasts.admin.session_disconnected', { target: target.email ?? target.id }));
      setTarget(null);
    } catch (err) {
      toast.error(err instanceof Error && err.message.trim() ? err.message : i18n.t('common:toasts.unexpected_error'));
    } finally {
      setActing(false);
    }
  };

  const handleDisconnectAll = async () => {
    setActing(true);
    try {
      await disconnectAll();
      toast.success(i18n.t('common:toasts.admin.all_sessions_disconnected'));
      setShowDisconnectAll(false);
    } catch (err) {
      toast.error(err instanceof Error && err.message.trim() ? err.message : i18n.t('common:toasts.unexpected_error'));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading sessions…"
      />
    );
  }

  return (
    <div className="flex-1 min-h-0 h-full">
      <DataTableOrganism<SessionTableRow>
        columns={columns}
        data={tableRows}
        pageSize={10}
        fillHeight
        enableSelection
        enableSearch
        enableColumnVisibility
        columnVisibilityLabel="Column visibility"
        toolbarTitle={<AccentBarHeading>Sessions</AccentBarHeading>}
        searchPlaceholder="Search by email, name, or sub…"
        searchKeys={['email', 'name', 'id']}
        paginationItemLabel="users"
        toolbarEndSlot={
          <Button
            type="contained"
            intent="danger"
            size="md"
            label="Disconnect all sessions"
            icon={<AlertTriangle />}
            onClick={() => setShowDisconnectAll(true)}
          />
        }
        dataTableProps={{ emptyState }}
      />

      <ConfirmationDialog
        isOpen={target !== null}
        onClose={() => !acting && setTarget(null)}
        title="Force disconnect user"
        message={
          target
            ? `${target.email ?? target.id} will be logged out from all sessions. Their next request will return 401 and they will be redirected to the login page. They can immediately log back in.`
            : ''
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => !acting && setTarget(null),
            type: 'outlined',
            intent: 'secondary',
            disabled: acting,
          },
          {
            label: 'Disconnect',
            onClick: handleDisconnectUser,
            type: 'contained',
            intent: 'danger',
            loading: acting,
          },
        ]}
      />

      <ConfirmationDialog
        isOpen={showDisconnectAll}
        onClose={() => !acting && setShowDisconnectAll(false)}
        title="Disconnect all sessions"
        message="Every user will be logged out except you. Other admins will also be disconnected. Use this for emergency containment or after a security incident."
        buttons={[
          {
            label: 'Cancel',
            onClick: () => !acting && setShowDisconnectAll(false),
            type: 'outlined',
            intent: 'secondary',
            disabled: acting,
          },
          {
            label: 'Disconnect all',
            onClick: handleDisconnectAll,
            type: 'contained',
            intent: 'danger',
            loading: acting,
          },
        ]}
      />
    </div>
  );
};
